<template>
  <asc-card
    flat
    :class="[
      'asc-fleet-tail-card',
      fullSize ? 'asc-fleet-tail-card--full-size' : null,
    ]"
    no-spacing
  >
    <div class="asc-fleet-tail-card--content">
      <template v-if="isValidItem">
        <div class="asc-fleet-tail-card--icons">
          <div class="asc-fleet-tail-card--icon1">
            <template v-if="!disableOpenMaintenance && item.issuesCount > 0">
              <v-badge
                :content="item.issuesCount"
                :value="item.issuesCount > 0"
                :color="getBackgroundColor"
              >
                <v-tooltip
                  bottom
                  :disabled="item.issuesCount === 0"
                  content-class="fleet-tooltip-content"
                  color="#333333"
                >
                  <template #activator="{ on, attrs }">
                    <v-icon
                      class="asc-fleet-tail-card--enabled-icon"
                      size="25"
                      v-bind="attrs"
                      :color="getIssuesColor"
                      v-on="on"
                      @click="handleIssuesClick"
                    >
                      mdi-alert-circle
                    </v-icon>
                  </template>
                  <span>{{ getIssuesTooltip }}</span>
                </v-tooltip>
              </v-badge>
            </template>
            <template v-if="!disableOpenMaintenance && item.issuesCount === 0">
              <v-icon class="asc-fleet-tail-card--disabled-icon" size="25">
                mdi-alert-circle
              </v-icon>
            </template>
            <template v-if="disableOpenMaintenance && item.issuesCount > 0">
              <v-badge
                :content="item.issuesCount"
                :value="item.issuesCount > 0"
                :color="getBackgroundColor"
              >
                <v-tooltip
                  bottom
                  :disabled="item.issuesCount === 0"
                  content-class="fleet-tooltip-content"
                  color="#333333"
                >
                  <template #activator="{ on, attrs }">
                    <v-icon
                      class="asc-fleet-tail-card--read-only-icon"
                      size="25"
                      v-bind="attrs"
                      :color="getIssuesColor"
                      v-on="on"
                    >
                      mdi-alert-circle
                    </v-icon>
                  </template>
                  <span>{{ getIssuesTooltip }}</span>
                </v-tooltip>
              </v-badge>
            </template>
          </div>
          <div class="asc-fleet-tail-card--icon2">
            <template v-if="item.fdeCount > 0">
              <v-badge
                :content="item.fdeCount"
                :value="item.fdeCount > 0"
                :color="getBackgroundColor"
              >
                <v-tooltip
                  bottom
                  :disabled="item.fdeCount === 0"
                  color="#333333"
                  content-class="fleet-tooltip-content"
                >
                  <template #activator="{ on, attrs }">
                    <v-icon
                      class="asc-fleet-tail-card--enabled-icon"
                      size="25"
                      v-bind="attrs"
                      :color="getFdesColor"
                      v-on="on"
                      @click="handleFdesClick"
                    >
                      mdi-alert
                    </v-icon>
                  </template>
                  <span>
                    {{ getFdesTooltip }}
                  </span>
                </v-tooltip>
              </v-badge>
            </template>
            <template v-if="item.fdeCount === 0">
              <v-icon class="asc-fleet-tail-card--disabled-icon" size="25">
                mdi-alert
              </v-icon>
            </template>
          </div>

          <div class="asc-fleet-tail-card--icon3">
            <template v-if="item.daysOffline > 2">
              <v-badge
                :content="item.daysOffline"
                :value="item.daysOffline > 2"
                :color="getBackgroundColor"
              >
                <v-tooltip
                  bottom
                  :disabled="item.daysOffline < 3"
                  color="#333333"
                  content-class="fleet-tooltip-content"
                >
                  <template #activator="{ on, attrs }">
                    <v-icon
                      class="asc-fleet-tail-card--enabled-icon"
                      size="25"
                      v-bind="attrs"
                      :color="getWifiColor"
                      v-on="on"
                      @click="handleConnectivityClick"
                    >
                      mdi-wifi-strength-off
                    </v-icon>
                  </template>
                  <span>{{ getWifiTooltip }}</span>
                </v-tooltip>
              </v-badge>
            </template>
            <template v-if="item.daysOffline < 3">
              <v-icon class="asc-fleet-tail-card--disabled-icon" size="25">
                mdi-wifi-strength-off
              </v-icon>
            </template>
          </div>
        </div>
        <div class="asc-fleet-tail-card--name">
          <asc-label size="xxl" :value="item.name" uppercase></asc-label>
        </div>
        <div class="asc-fleet-tail-card--master-model">
          <asc-label size="md" :value="item.masterModelName"></asc-label>
        </div>
      </template>
    </div>
  </asc-card>
</template>

<script>
import AscCard from '../atoms/AscCard.vue'
import AscLabel from '../atoms/AscLabel.vue'

import variables from '@/styles/_varsJs.scss'

export default {
  name: 'AscFleetCard',

  components: {
    AscCard,
    AscLabel,
  },

  props: {
    item: {
      type: Object,
      default: () => {},
    },
    fullSize: {
      type: Boolean,
      default: false,
    },
    isIssueInvestigator: {
      type: Boolean,
      default: false,
    },
    isIssueReader: {
      type: Boolean,
      default: false,
    },
    isIssueUpdater: {
      type: Boolean,
      default: false,
    },
    disableOpenMaintenance: {
      type: Boolean,
      default: false,
    },
    onOpenMaintenance: {
      type: Function,
      default: () => {},
    },
    onOpenFde: {
      type: Function,
      default: () => {},
    },
    onOpenConnectivity: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      wifi: null,
    }
  },

  computed: {
    getBackgroundColor() {
      return variables.gray0
    },

    isValidItem() {
      return this.item !== null
    },

    getIssuesColor() {
      return this.item.issuesCount > 0
        ? variables.warningbase
        : variables.gray300
    },

    getFdesColor() {
      return this.item.fdeCount > 0 ? variables.warningbase : variables.gray300
    },

    getWifiColor() {
      if (this.item.daysOffline < 3) {
        return variables.gray300
      }

      return this.item.daysOffline >= 7
        ? variables.warningbase
        : variables.cautionbase
    },

    getFdesTooltip() {
      const trailingS = this.item.fdeCount > 1 ? 's' : ''
      return `${this.item.name} has ${this.item.fdeCount} Flight Deck Effect${trailingS} on Flight ${this.item.fdeFlightNumber} ${this.item.fdeFormattedFlightStart}`
    },

    getIssuesTooltip() {
      return `${this.item.name} has ${this.item.issuesCount} associated open Maintenance Recommendation(s)`
    },

    getWifiTooltip() {
      return `It has been ${this.item.daysOffline} days since data was last received for ${this.item.name}`
    },
  },

  methods: {
    handleFdesClick() {
      if (this.onOpenFde) {
        this.onOpenFde(this.item)
      }
    },

    handleIssuesClick() {
      if (this.onOpenMaintenance) {
        this.onOpenMaintenance(this.item.name)
      }
    },

    handleConnectivityClick() {
      if (this.onOpenConnectivity) {
        this.onOpenConnectivity(this.item.name, this.item.daysOffline)
      }
    },
  },
}
</script>

<style lang="scss">
.fleet-tooltip-content {
  font-size: 12px;
  border-radius: 2px;
  padding: 8px 10px;
  opacity: 1 !important;
}

.asc-fleet-tail-card {
  margin: 8px;
  width: 120px;
  height: 110px;

  .asc-card__text {
    height: 100%;
  }

  &--content {
    padding: 8px;
    background-color: $gray-200;
  }

  &--icons {
    display: flex;
    margin-top: 4px;
    margin-bottom: 12px;

    .v-badge {
      .v-icon {
        margin-top: 4px;
      }

      .v-badge__badge {
        margin-top: -5px;
        margin-left: 10px;
        color: $primary-black !important;
        inset: inherit !important;
      }
    }

    .asc-fleet-tail-card--enabled-icon:hover {
      background-color: $gray-400;
    }

    .asc-fleet-tail-card--disabled-icon {
      color: $gray-300;
      margin-top: 4px;
    }

    .asc-fleet-tail-card--read-only-icon {
      color: $caution-base;
      margin-top: 4px;
    }
  }

  &--icon1 {
    margin-left: -2px;
  }

  &--icon2 {
    margin-left: 10px;
  }

  &--icon3 {
    margin-left: 10px;
  }

  &--name {
    margin-bottom: 8px;
  }
}

.asc-fleet-tail-card--full-size {
  height: 100%;
  margin: 1px 0 0 0;
  padding-bottom: 1px;
  width: inherit;

  .asc-fleet-tail-card--content {
    height: 100%;
  }
}
</style>
